import React from "react";
import { CSSTransition } from "react-transition-group";
import PropTypes from "prop-types";
import {
  Button,
  Checkbox,
  ProgressSlider,
  Box,
  Area,
  TextButton,
  Select,
  EditableBox,
  EditableSelect,
  EditableArea,
  EditableDate,
  EditableTime,
  EditSizeLabel,
  EditSizeLink,
} from "./Input";
import Webcam from "../components-static/Webcam";
import u from "../utilities/Utilities";
import moment from "moment";
import { Link } from "react-router-dom";
import store from "store";
import { Photo, Spreadsheet } from "../data/Mimetypes";
import Editor from "react-avatar-editor";
import "../styles/common.scss";
import "../styles/animations.scss";
import Measure from "react-measure";

/**
 * Creates a themed segment element
 *
 * @param {bool} active
 *      show or hide this component
 *
 * @param {string} align
 *      CSS text align - left | right | center
 *
 * @param {string} padding
 *      CSS padding
 *
 * @param {string}  margin
 *      CSS margin
 *
 * @param {string} scrollAnchor
 *      id of an element on screen, if provided, screen will scroll to element on active
 */
export class Segment extends React.Component {
  scrolledOnce = false;

  componentDidUpdate = (prevProps, prevState) => {
    if (!this.props.active) {
      this.scrolledOnce = false;
    }
  };

  scrollToDiv = () => {
    if (!this.scrolledOnce) {
      this.scroll();
      this.scrolledOnce = true;
    }
  };

  scroll = () => {
    if (this.props.scrollAnchor) {
      var element = document.getElementById(this.props.scrollAnchor);
      if (typeof element == "undefined") {
        return 0;
      }
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  render = () => {
    return (
      <CSSTransition
        in={this.props.active}
        classNames={this.props.noAnimation ? "noAnim" : "slideIn"}
        timeout={300}
      >
        <div id={this.props.scrollAnchor} style={{ padding: "1px 0" }}>
          {this.props.active ? (
            <div
              ref={(a) => {
                this.scrollToDiv();
              }}
              className="segment-container"
              style={
                this.props.style
                  ? this.props.style
                  : {
                      textAlign: this.props.align,
                      padding: this.props.padding,
                      margin: this.props.margin,
                    }
              }
            >
              {this.props.children}
            </div>
          ) : (
            <div />
          )}
        </div>
      </CSSTransition>
    );
  };
}
Segment.propTypes = {
  active: PropTypes.bool.isRequired,
  align: PropTypes.string,
  padding: PropTypes.string,
  margin: PropTypes.string,
  noAnimation: PropTypes.bool,
  style: PropTypes.object,
  scrollAnchor: PropTypes.string,
  id: PropTypes.string,
};
Segment.defaultProps = {
  align: "left",
  padding: "0 0 0 0",
  margin: "0 0 0 0",
  noAnimation: false,
};

/**
 * Creates a themed databox element for data entities
 *
 * @param {bool} active
 *      show or hide this component
 *
 * @param {string} align
 *      CSS text align - left | right | center
 *
 * @param {string} padding
 *      CSS padding
 *
 * @param {string}  margin
 *      CSS margin
 */
export class Databox extends React.Component {
  render = () => {
    if (!this.props.active) {
      return "";
    }

    return (
      <div
        className={"data-box" + (this.props.hideBorder ? " no-border" : "")}
        style={{
          margin: this.props.margin,
          textAlign: this.props.align,
          padding: this.props.padding,
          width: this.props.width,
          maxWidth: this.props.maxWidth,
          height: this.props.height,
          maxHeight: this.props.maxHeight,
        }}
      >
        {this.props.title ? (
          <div className="data-box-title">
            <h2 className="data-box-title-content">{this.props.title}</h2>
          </div>
        ) : (
          ""
        )}
        <div className="data-box-content">{this.props.children}</div>
      </div>
    );
  };
}
Databox.propTypes = {
  active: PropTypes.bool.isRequired,
  align: PropTypes.string,
  padding: PropTypes.string,
  margin: PropTypes.string,
  width: PropTypes.string,
  maxWidth: PropTypes.string,
  hideBorder: PropTypes.bool,
  title: PropTypes.string,
  height: PropTypes.string,
  maxHeight: PropTypes.string,
};
Databox.defaultProps = {
  align: "left",
  margin: "50px auto",
};

/**
 * Creates a themed Databox Item
 *
 * @param {string} label
 *      label that is shown in the databox item
 */
export class DataboxItem extends React.Component {
  render = () => {
    return (
      <div className="data-box-item">
        <div className="data-box-item-label">{this.props.label}</div>
        <div className="data-box-item-input">{this.props.children}</div>
      </div>
    );
  };
}
DataboxItem.propTypes = {
  label: PropTypes.string.isRequired,
};

/**
 * Creates a themed databox item that supports generic setup of certain components
 *
 * @param {object} settings
 *      @var {string} url
 *
 *      @var {bool} enabled
 *
 *      @var {function} onChange
 *
 *      @var {object || string} id
 *
 * @param {object} content
 *      respective content object
 *
 * @param {object} fields
 *      @var {string} databoxType
 *          image,label,link,box,select,date,time,area
 *
 *      @var {string} label
 *
 *      @var {string} field
 *
 *      @var {string} value
 *
 *      @var {object} rules
 *
 *      @var {string} to
 *
 *      @var {string} options
 *          Select only
 *
 *      @var {string} type
 *          Box only - defaults to text
 *
 *      @var {array} range
 *          Date only - defaults to [5,5]
 *
 * @param {object} validation
 *      See validation folder
 */
export class DataboxGeneral extends React.Component {
  render = () => {
    const settings = this.props.settings;
    const content = this.props.content;
    const fields = this.props.fields;
    const validation = this.props.validation;
    const rules = this.props.validation
      ? Object.assign({ nullText: "None" }, this.props.validation[fields.field])
      : { nullText: "None" };
    const enabled =
      typeof fields.enabled != "undefined" ? fields.enabled : settings.enabled;
    const title = enabled
      ? fields.title
      : fields.disabledTitle
      ? fields.disabledTitle
      : fields.title;

    if (fields.databoxType == "image") {
      return <DataboxPhoto image={content[fields.field]} />;
    }

    return (
      <div className="data-box-item" title={title}>
        <div className="data-box-item-label">
          <div style={{ display: "inline-block", verticalAlign: "middle" }}>
            {fields.label}
          </div>
          {title ? (
            <img
              src="/images/input/info.png"
              className="info-tooltip"
              title={title}
            />
          ) : (
            ""
          )}
        </div>
        <div className="data-box-item-input">
          {fields.databoxType == "label" ? (
            <EditSizeLabel nullText="None">
              {content[fields.field]}
            </EditSizeLabel>
          ) : (
            ""
          )}
          {fields.databoxType == "link" ? (
            <EditSizeLink>
              {content[fields.field] ? (
                <Link
                  onClick={fields.onClick}
                  onContextMenu={fields.onClick}
                  to={fields.to}
                >
                  {content[fields.field]}
                </Link>
              ) : (
                "None"
              )}
            </EditSizeLink>
          ) : (
            ""
          )}
          {fields.databoxType == "box" ? (
            <EditableBox
              values={{ value: content[fields.field], enabled: enabled }}
              type={fields.type}
              rules={rules}
              field={fields.field}
              id={settings.id}
              label={fields.label}
              url={settings.url}
              onChange={settings.onChange}
            />
          ) : (
            ""
          )}
          {fields.databoxType == "select" ? (
            <EditableSelect
              values={{
                value: content[fields.field],
                options: fields.options,
                enabled: enabled,
              }}
              rules={rules}
              field={fields.field}
              id={settings.id}
              label={fields.label}
              url={settings.url}
              onChange={settings.onChange}
            />
          ) : (
            ""
          )}
          {fields.databoxType == "date" ? (
            <EditableDate
              values={{ value: content[fields.field], enabled: enabled }}
              range={fields.range}
              rules={rules}
              field={fields.field}
              id={settings.id}
              label={fields.label}
              url={settings.url}
              onChange={settings.onChange}
            />
          ) : (
            ""
          )}
          {fields.databoxType == "time" ? (
            <EditableTime
              values={{ value: content[fields.field], enabled: enabled }}
              range={fields.range}
              rules={rules}
              field={fields.field}
              id={settings.id}
              label={fields.label}
              url={settings.url}
              onChange={settings.onChange}
            />
          ) : (
            ""
          )}
          {fields.databoxType == "area" ? (
            <EditableBox
              values={{ value: content[fields.field], enabled: enabled }}
              rules={rules}
              field={fields.field}
              id={settings.id}
              label={fields.label}
              url={settings.url}
              onChange={settings.onChange}
            />
          ) : (
            ""
          )}
        </div>
      </div>
    );
  };
}
DataboxGeneral.propTypes = {
  settings: PropTypes.object.isRequired,
  fields: PropTypes.object.isRequired,
  content: PropTypes.object,
  validation: PropTypes.object,
};
DataboxGeneral.defaultProps = {
  settings: {},
  content: {},
  validation: {},
  fields: {},
};

/**
 * Creates a themed Databox Photo Container
 *
 * @param {string} image
 *      image src to be displayed inside this container
 */
export class DataboxPhoto extends React.Component {
  render = () => {
    if (typeof this.props.image == "undefined") {
      return "";
    }
    return (
      <div className="data-box-photo">
        <div className="data-box-photo-left" />
        <div className="data-box-photo-background">
          <div className="data-box-photo-background-cover" />
          <img src={this.props.image} className="data-box-image" />
        </div>
        <div className="data-box-photo-right" />
      </div>
    );
  };
}
DataboxPhoto.propTypes = {
  image: PropTypes.string,
};

/**
 * Creates a placeholder object to help layout databox items
 *
 * @param {number} length
 *      number of consecutive placeholder boxes in a row to account for resizing layout
 */
export class DataboxPlaceholder extends React.Component {
  render = () => {
    return (
      <div
        className={
          "data-box-placeholder " + (this.props.length == 1 ? "one" : "two")
        }
      >
        <div className="data-box-item-label" />
        <div className="data-box-item-input" />
      </div>
    );
  };
}
DataboxPlaceholder.propTypes = {
  length: PropTypes.number.isRequired,
};

/**
 * Creates an animated step division to unify animation between steps
 *
 * @param {bool} active
 *      show or hide this step
 *
 * @param {string} scrollAnchor
 *      id of an element on screen, if provided, screen will scroll to element on active
 *
 * @param {bool} forceFade
 *      Changes step animation to fade instead of slide, performance improvement for devices that cannot support slide
 */
export class Step extends React.Component {
  scrolledOnce = true;

  componentDidUpdate = (prevProps, prevState) => {
    if (!this.props.active) {
      this.scrolledOnce = false;
    }
  };

  scrollToDiv = () => {
    if (this.props.scrollAnchor && !this.scrolledOnce) {
      var element = document.getElementById(this.props.scrollAnchor);

      if (typeof element == "undefined") {
        return 0;
      }
      setTimeout(() => {
        //console.log("scrolling to top", this.props.scrollAnchor, element);
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);

      this.scrolledOnce = true;
    }
  };

  render = () => {
    return (
      <CSSTransition
        in={this.props.active}
        classNames={this.props.forceFade ? "fadeIn" : "slideInVanish"}
        timeout={300}
      >
        {this.props.active == true ? (
          <div
            ref={(a) => {
              this.scrollToDiv();
            }}
          >
            {this.props.children}
          </div>
        ) : (
          <div />
        )}
      </CSSTransition>
    );
  };
}
Step.propTypes = {
  active: PropTypes.bool.isRequired,
  scrollAnchor: PropTypes.string,
  forceFade: PropTypes.bool,
};

/**
 * Creates a themed header object
 *
 * @param {string} type
 *      half || full
 *      half = only show text logo
 *      full = show text logo and icon logo
 *
 * @param {array} buttons
 *      Navigational buttons shown on this page - See data/NavButtons.js
 *
 * @param {bool} hasNav
 *      show navigational bar?
 *
 * @param {number} width
 *      the current inner width of the browser
 *
 * @param {string} current
 *      the current url of the web page
 */
export class Header extends React.Component {
  state = {
    avatarLink: u.getUserAvatar(),
    name: u.getFullName(),
    favorite: u.getFavorite(),
    tempUser: true,
  };

  componentDidMount = () => {
    this.setState({ tempUser: store.get("tempUser") });
  };

  onLogout = (e) => {
    e.preventDefault();
    u.logout();
  };

  onMarkFavorite = (e) => {
    e.preventDefault();

    let favorite = this.props.current;
    if (favorite == "/estate-list") {
      favorite = "/estate-list/owner";
    }
    store.set("favorite", favorite);
    this.setState({ favorite: favorite });
    u.post({
      url: "/api/set-favorite",
      data: {
        favorite: favorite,
      },
      success: (callback) => {},
      error: (error, status) => {},
    });
  };

  onUnmarkFavorite = (e) => {
    e.preventDefault();

    const favorite = "/dashboard";
    store.set("favorite", favorite);
    this.setState({ favorite: favorite });
    u.post({
      url: "/api/set-favorite",
      data: {
        favorite: favorite,
      },
      success: (callback) => {},
      error: (error, status) => {},
    });
  };

  onSiteBack = () => {
    window.history.back();
  };

  render = () => {
    const c = this.props.type != "logo" ? " mobile" : "";
    const favorite = this.state.favorite ? this.state.favorite : "/login";

    let hasMenu = false;
    if (this.props.buttons) {
      for (var i = 0; i < this.props.buttons.length; i++) {
        if (this.props.buttons[i].permission != "none") {
          hasMenu = true;
          break;
        }
      }
    }

    return (
      <div className={"header-container" + c}>
        {this.props.hasNav && !this.state.tempUser ? (
          <div className="header-addon no-print">
            {this.props.current != "/dashboard" ? (
              <div className="header-back" onClick={this.onSiteBack}>
                <img src="/images/mobile/left-arrow.png" />
              </div>
            ) : (
              ""
            )}

            {hasMenu ? (
              <div className="header-nav">
                <NavMenu
                  buttons={this.props.buttons}
                  current={this.props.current}
                />
              </div>
            ) : (
              ""
            )}

            <div className={"header-profile" + (hasMenu ? "" : " no-nav")}>
              {this.state.avatarLink ? (
                <img
                  src={this.state.avatarLink}
                  style={{ marginRight: "10px" }}
                />
              ) : null}
              <div className="header-text">{this.state.name}</div>
            </div>

            <div className="header-options">
              <img
                src="/images/app/logout.png"
                title="Logout"
                onClick={this.onLogout}
              />

              <Link to={favorite}>
                <img
                  className="header-home"
                  src="/images/app/home.png"
                  title="Return to favorite page"
                />
              </Link>

              {this.props.current == this.state.favorite ? (
                <img
                  className="header-star"
                  src="/images/app/star.png"
                  title="Unmark favorite page"
                  onClick={this.onUnmarkFavorite}
                />
              ) : (
                <img
                  className="header-star"
                  src="/images/app/unstar.png"
                  title="Mark page as favorite"
                  onClick={this.onMarkFavorite}
                />
              )}
            </div>
          </div>
        ) : (
          ""
        )}
        <img src="/images/app/app-logo-small.png" className="app-logo" />
        {this.props.pageTitle ? (
          <div className="mobile-title">
            {this.props.pageTitle.toUpperCase()}
          </div>
        ) : (
          ""
        )}
      </div>
    );
  };
}
Header.propTypes = {
  type: PropTypes.string.isRequired,
  hasNav: PropTypes.bool,
  buttons: PropTypes.array,
  current: PropTypes.string,
};

/**
 * Creates a themed dynamic navigation menu for desktop users > 650px browsers
 *
 * @param {string} current
 *      url of the current page
 *
 * @param {array} buttons
 *      all buttons required to render on this page
 *
 */
class NavMenu extends React.Component {
  state = {
    active: false,
  };

  onClickHamburger = (e) => {
    e.preventDefault();
    this.setState({ active: !this.state.active });
  };

  onLogout = (e) => {
    if (e) {
      e.preventDefault();
    }
    u.logout();
  };

  render = () => {
    return (
      <div className="nav-menu">
        <div
          className={"hamburger-nav" + (this.state.active ? " cross" : "")}
          onClick={this.onClickHamburger}
        >
          <svg className="hamburger-svg" viewBox="0 0 800 600">
            <path
              d="M300,220 C300,220 520,220 540,220 C740,220 640,540 520,420 C440,340 300,200 300,200"
              id="top"
            ></path>
            <path d="M300,320 L540,320" id="middle"></path>
            <path
              d="M300,210 C300,210 520,210 540,210 C740,210 640,530 520,410 C440,330 300,190 300,190"
              id="bottom"
              transform="translate(480, 320) scale(1, -1) translate(-480, -318) "
            ></path>
          </svg>
        </div>
        {this.state.active ? (
          <div className="nav-menu-container">
            <img className="nav-triangle" src="/images/app/nav-triangle.png" />
            <div className="nav-triangle-cover" />
            <div className="nav-menu-content">
              {this.props.buttons.map((item, index) => {
                return (
                  <Link key={index} to={item.url} className="nav-menu-item">
                    <div
                      className={
                        "nav-item-image" +
                        (index % 2 == 0 ? " col-even" : " col-odd") +
                        (item.url == this.props.current ? " current" : "")
                      }
                    >
                      <img src={item.image} />
                    </div>
                    <div className="nav-item-text">{item.name}</div>
                  </Link>
                );
              })}

              <div
                className="nav-menu-item nav-addon"
                onClick={this.onLogout}
                style={{ cursor: "pointer" }}
              >
                <div className={"nav-item-image"}>
                  <img src="/images/dashboard/logout.png" />
                </div>
                <div className="nav-item-text">Logout</div>
              </div>
            </div>
          </div>
        ) : (
          ""
        )}
      </div>
    );
  };
}
NavMenu.propTypes = {
  current: PropTypes.string.isRequired,
  buttons: PropTypes.array.isRequired,
};

/**
 * Creates a themed mobile nav menu
 *
 * @param {array} content
 *     @var {string} label
 *     @var {string} mobileLabel
 *     @var {string} ref
 *     @var {string} image
 *     @var {bool} enabled
 *
 * @param {bool} hasNotebook
 *      Does that page have a notebook module
 *
 * @param {func} onClick
 *      built to function with onMenuOptionsClicked(ref,show,mobileNav)
 */
export class MobileMenu extends React.Component {
  state = { active: false };

  onToggleActive = (e) => {
    if (e) {
      e.preventDefault();
    }
    this.setState({ active: !this.state.active });
  };

  onSelectItem = (e) => {
    const index = parseInt(e.currentTarget.id);
    const array = this.props.content.filter((a) => {
      return a.enabled;
    });
    const target = array[index];
    this.props.onClick(target.ref, true, array);
    this.setState({ active: false });
  };

  render = () => {
    const content = this.props.content.filter((a) => {
      return a.enabled;
    });
    if (content.length == 0) {
      return <span />;
    }

    return (
      <div
        className={
          "mobile-menu" +
          (this.props.hasNotebook ? " short" : "") +
          (this.state.active ? " active" : "")
        }
      >
        <div className="mobile-menu-title" onClick={this.onToggleActive}>
          <img
            style={{ width: "20px", height: "20px", margin: "5px auto" }}
            src="/images/mobile/action.png"
          />
        </div>
        <div className="mobile-menu-content">
          {content.map((item, index) => {
            return (
              <div
                key={index}
                id={index + ""}
                className="mobile-menu-item"
                onClick={this.onSelectItem}
              >
                {(item.mobileLabel
                  ? item.mobileLabel
                  : item.label
                ).toLocaleUpperCase()}
              </div>
            );
          })}
        </div>
      </div>
    );
  };
}
MobileMenu.propTypes = {
  content: PropTypes.array.isRequired,
  hasNotebook: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

/**
 * Creates a themed footer object
 *
 * @param type
 *      half || full
 *      half = only show chimeric logo
 *      full = show chimeric logo & copyright text
 *
 * @param width
 *      inner width of the browser
 */
export class Footer extends React.Component {
  state = { year: moment().format("YYYY") };

  render = () => {
    return (
      <div className="footer-container" style={this.props.style}>
        <div className="footer">
          {this.props.children ? this.props.children : ""}
          {this.props.type != "half" ? (
            <div className="footer-text no-print">
              Copyright &copy; {this.state.year} Chimeric Technologies. All
              Rights Reserved.
            </div>
          ) : (
            ""
          )}
          <img src="/images/app/footer.png" className="footer-logo no-print" />
        </div>
      </div>
    );
  };
}
Footer.propTypes = {
  type: PropTypes.string,
};

/**
 * Creates a themed file drag and drop container that auto validates & uploads files
 *
 * @param {string} label
 *      name of the parent component
 *
 * @param {bool} noUpload
 *      if false, file will not upload
 *
 * @param {bool} accessibleForPreview
 *      if true, stores file in a URL-accessible location on server
 *      else stores in protected location
 *
 * @param {array} filetypes
 *      list of acceptable filetypes for this dropzone. See data/Mimetypes
 *
 * @param {function} onAdded (filename,file)
 *      file added to dropzone callback function
 *
 * @param {function} onProgress (progress)
 *      callback on upload progress intervals
 *
 * @param {function} onUploaded(filename)
 *      successful upload callback with server filename
 *
 * @param {function} onError(error)
 *      callback on upload error
 *
 * @function {null} onCancel
 *      call to terminate upload sequence
 *
 */
export class Dropzone extends React.Component {
  state = {
    status: "idle",
    progress: 0,
    ext: "",
    browserFile: null,
  };

  uploadEvent = null;

  componentDidMount = () => {
    let fileExtension = "";
    for (var i = 0; i < this.props.filetypes.length; i++) {
      for (var j = 0; j < this.props.filetypes[i].ext.length; j++) {
        fileExtension =
          fileExtension +
          (i == 0 && j == 0 ? "" : ", ") +
          "." +
          this.props.filetypes[i].ext[j];
      }
    }
    this.setState({ ext: fileExtension });
  };

  onDragEnter = (e) => {
    e.stopPropagation();
    e.preventDefault();
    this.setState({ status: "drag-enter" });
  };

  onDragLeave = (e) => {
    e.stopPropagation();
    e.preventDefault();
    this.setState({ status: "drag-leave" });
  };

  onDragOver = (e) => {
    e.stopPropagation();
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  onDrop = (e) => {
    e.stopPropagation();
    e.preventDefault();
    this.setState({ status: "idle", progress: 0, error: "" });
    let files = e.dataTransfer.files;
    this.onSubmit(files);
  };

  onCancel = () => {
    if (this.uploadEvent) {
      this.uploadEvent.cancel();
      this.uploadEvent = null;
      this.setState({ error: "Upload cancelled..." });
    }
  };

  onClick = (e) => {
    const element = this.props.label + "-input";
    const fileBrowser = document.getElementById(element);
    fileBrowser.click();
  };

  onSelectFile = (e) => {
    const element = this.props.label + "-input";
    const fileBrowser = document.getElementById(element);
    this.onSubmit(fileBrowser.files);
    e.currentTarget.value = null;
  };

  onSubmit = (files) => {
    this.setState({ error: "" });
    if (files.length > 1) {
      this.setState({ error: "Cannot upload more than 1 file at a time" });
    }

    //validate file type
    let validFile = false;
    let fileImageReference = "";
    let filetype = "";
    const fileExtension = files[0].name.substr(
      files[0].name.lastIndexOf(".") + 1
    );

    for (var i = 0; i < this.props.filetypes.length; i++) {
      if (files[0].type == this.props.filetypes[i].mimetype) {
        validFile = true;
        fileImageReference = this.props.filetypes[i].fileImageReference;
        filetype = this.props.filetypes[i].type;
        break;
      }

      if (!validFile) {
        for (var j = 0; j < this.props.filetypes[i].ext.length; j++) {
          if (fileExtension.toLowerCase() == this.props.filetypes[i].ext[j]) {
            validFile = true;
            fileImageReference = this.props.filetypes[i].fileImageReference;
            filetype = this.props.filetypes[i].type;
            break;
          }
        }
      }
    }

    if (!validFile) {
      this.setState({
        error: "File type - " + files[0].type + " is not supported",
      });
      return 0;
    }

    if (this.props.onAdded) {
      this.props.onAdded(files[0].name, files[0]);
    }

    if (this.props.noUpload) {
      return 0;
    }

    this.uploadEvent = u.upload({
      file: files[0],
      data: {
        accessibleForPreview: this.props.accessibleForPreview,
      },
      uploadProgress: (e) => {
        if (e.lengthComputable) {
          let loaded = Math.round((e.loaded / e.total) * 100);
          if (this.props.onProgress) {
            this.props.onProgress(loaded);
          }
        }
      },
      success: (filename) => {
        this.props.onUploaded(filename, fileImageReference, filetype);
      },
      error: (error, status) => {
        this.props.onError(error);
      },
    });
  };

  render = () => {
    return (
      <div
        className={"dropzone " + this.state.status}
        onDragEnter={this.onDragEnter}
        onDragLeave={this.onDragLeave}
        onDragOver={this.onDragOver}
        onDrop={this.onDrop}
        onClick={this.onClick}
      >
        <input
          id={this.props.label + "-input"}
          type="file"
          style={{ display: "none" }}
          accept={this.state.ext}
          onChange={this.onSelectFile}
        />
        <div className="segment-error">{this.state.error}</div>
        <h3 className="dropzone-title">Drag & Drop File or Click Here</h3>
        <div className="dropzone-formats">Accepts formats {this.state.ext}</div>
      </div>
    );
  };
}
Dropzone.propTypes = {
  label: PropTypes.string.isRequired,
  accessibleForPreview: PropTypes.bool.isRequired,
  onAdded: PropTypes.func,
  onUploaded: PropTypes.func,
  onError: PropTypes.func.isRequired,
  onProgress: PropTypes.func,
  filetypes: PropTypes.arrayOf(PropTypes.object).isRequired,
  noUpload: PropTypes.bool,
};

/**
 * Creates an image dropzone including image editor
 *
 * @param {func} onUpload (filename,callback)
 *
 */
export class ImageDropzone extends React.Component {
  state = {
    step: 0,
    rawfile: "",
    scale: 100,
    rotate: 0,
    loading: false,
    loadText: "Uploading...",
  };

  uploadEvent = null;

  onFileAdded = (filename, file) => {
    this.setState({ rawfile: file, step: 1 });
  };

  onFileError = (error) => {
    this.setState({ error: error });
  };

  onScaleChange = (value) => {
    this.setState({ scale: value });
  };

  onRotateChange = (value) => {
    this.setState({ rotate: value });
  };

  onBack = () => {
    this.setState({ step: 0, scale: 100, rotate: 0 });
  };

  onConfirm = () => {
    this.setState({ loading: true });
    this.refs["editor"].getImage().toBlob(
      (blob) => {
        let f = new File([blob], "editted-image.png", {
          type: "image/png",
          lastModifier: moment().format("YYYY-MM-DD"),
        });
        this.uploadEvent = u.upload({
          file: f,
          data: {
            accessibleForPreview: true,
          },
          uploadProgress: (e) => {
            if (e.lengthComputable) {
              let loaded = Math.round((e.loaded / e.total) * 100);
              this.setState({ loadText: "Uploading " + loaded + "%..." });
            }
          },
          success: (filename) => {
            this.setState({ step: 0, loadText: "Processing File..." });
            if (this.props.onUpload) {
              this.props.onUpload(filename, () => {
                this.setState({ loading: false });
              });
            }
          },
          error: (error, status) => {
            this.setState({ error: error });
          },
        });
      },
      "image/png",
      1
    );
  };

  render = () => {
    return (
      <div className="image-dropzone">
        <div className="image-dropzone-error">{this.state.error}</div>
        <Step active={this.state.step == 0}>
          <Dropzone
            label="image-dropzone"
            accessibleForPreview={false}
            onError={this.onFileError}
            onAdded={this.onFileAdded}
            filetypes={Photo}
            noUpload={true}
          />
          {this.props.onBack ? (
            <div className="list-segment-buttons">
              <Button type="medium" onClick={this.props.onBack}>
                Back
              </Button>
            </div>
          ) : (
            ""
          )}
        </Step>
        <Step active={this.state.step == 1}>
          <div className="image-dropzone-editor">
            <LoadGraphic
              active={this.state.loading}
              text={this.state.loadText}
            />
            <h3>Edit Image</h3>
            <Editor
              ref="editor"
              image={this.state.rawfile}
              width={256}
              height={256}
              border={30}
              color={[255, 0, 0, 0.3]} // RGBA
              scale={this.state.scale / 100}
              rotate={this.state.rotate}
            />
            <div className="image-dropzone-slider">
              <div className="image-dropzone-slider-title">Zoom</div>
              <ProgressSlider
                min={1}
                max={300}
                value={this.state.scale}
                onChange={this.onScaleChange}
              />
            </div>
            <div className="image-dropzone-slider">
              <div className="image-dropzone-slider-title">Rotate</div>
              <ProgressSlider
                min={-180}
                max={180}
                value={this.state.rotate}
                onChange={this.onRotateChange}
              />
            </div>
            <div className="image-dropzone-buttons">
              <Button type="medium" onClick={this.onBack}>
                Back
              </Button>
              <Button type="medium" onClick={this.onConfirm}>
                Confirm
              </Button>
            </div>
          </div>
        </Step>
      </div>
    );
  };
}
ImageDropzone.propTypes = {
  onUpload: PropTypes.func.isRequired,
  onBack: PropTypes.func,
};

/**
 * Creates a themed loading graphic to hide content and denote loading
 *
 * @param {bool} active
 *      show or hide this component
 *
 * @param {string} opacity
 *      [0..1] opacity of the background of this component
 *
 * @param {string} text
 *      text displayed under the animating load graphic
 */
export class LoadGraphic extends React.Component {
  render = () => {
    return this.props.active ? (
      <div className="spinner-container" style={this.props.style}>
        <div
          className="spinner-background"
          style={{ opacity: this.props.opacity }}
        ></div>
        <div className="spinner-vertical-align"></div>
        <div className="spinner-content">
          <div className="spinner">
            <div className="rect1"></div>
            <div className="rect2"></div>
            <div className="rect3"></div>
            <div className="rect4"></div>
            <div className="rect5"></div>
          </div>
          <div className="spinner-text">{this.props.text}</div>
        </div>
      </div>
    ) : null;
  };
}
LoadGraphic.propTypes = {
  active: PropTypes.bool.isRequired,
  opacity: PropTypes.string,
  text: PropTypes.string.isRequired,
};
LoadGraphic.defaultProps = {
  opacity: "1",
};

/**
 * Creates a menu bar on a page that allows you to create components as children and reference the components accordingly
 *
 * @param {function} onMenuOptionClicked
 *
 *      Use the following function in the parent page component
 *
 *      onMenuOptionClicked(ref,show){
 *           this.refs[ref].show(show);
 *       }
 *
 * @param {array} buttons
 *      buttons:[
 *               {label:"Add",ref:"add",image:"/images/page-menu/add.png",title:"Add new staff",enabled:true,hasComponent:true},
 *               {label:"Template",ref:"download",image:"/images/page-menu/template.png",title:"Download bulk add template",enabled:true,hasComponent:false},
 *               {label:"Delete",ref:"delete",image:"/images/page-menu/delete.png",title:"Delete selected staff",enabled:true,hasComponent:true},
 *               {label:"Spreadsheet",ref:"spreadsheet",image:"/images/page-menu/spreadsheet.png",title:"Generate spreadsheet from selection",enabled:true,hasComponent:true},
 *       ]
 */
export class PageMenu extends React.Component {
  state = { active: false, activeIndex: 0 };

  onClickButton = (e) => {
    const index = e.currentTarget.id;

    if (this.props.buttons[index].onClick) {
      this.props.buttons[index].onClick();
      return 0;
    }

    const ref = this.props.buttons[index].ref;
    const active = !this.state.active;
    this.props.onMenuOptionClicked(ref, active);
    this.setState({ active: active, activeIndex: index });
  };

  toggleForm = (ref, active) => {
    let index = -1;
    for (var i = 0; i < this.props.buttons.length; i++) {
      if (this.props.buttons[i].ref == ref) {
        index = i;
      }
    }
    this.props.onMenuOptionClicked(ref, active);
    this.setState({ active: active, activeIndex: index });
  };

  render = () => {
    const hasTitle = typeof this.props.title != "undefined";
    return (
      <div className="page-menu" id={this.props.id}>
        <h1 className="page-menu-title">{hasTitle ? this.props.title : ""}</h1>

        <div
          className={
            "page-menu-button-container" +
            (this.state.active ? " active" : "") +
            (hasTitle ? " has-title" : "")
          }
        >
          {this.props.buttons.map((button, index) => {
            if (
              (this.state.active && index != this.state.activeIndex) ||
              !button.enabled
            ) {
              return "";
            }

            return (
              <div
                className={
                  "page-menu-button " +
                  (!this.state.active && button.invisible ? " invisible" : "")
                }
                key={index}
                title={this.state.active ? "Cancel" : button.title}
                id={index}
                onClick={this.onClickButton}
              >
                <div
                  className={
                    "page-menu-button-image" +
                    (button.noBorder ? " no-border" : "")
                  }
                >
                  <img
                    src={
                      this.state.active
                        ? "/images/page-menu/cancel.png"
                        : button.image
                    }
                    style={{
                      padding: this.state.active ? undefined : button.padding,
                    }}
                  />
                </div>
                <div className="page-menu-button-title">
                  {this.state.active ? "" : button.label}
                </div>
              </div>
            );
          })}
        </div>
        <div className="page-menu-content">{this.props.children}</div>
      </div>
    );
  };
}
PageMenu.propTypes = {
  onMenuOptionClicked: PropTypes.func.isRequired,
  buttons: PropTypes.array.isRequired,
  id: PropTypes.string,
  title: PropTypes.string,
};

/**
 * Creates a page menu item for deleting list entries.
 *
 * @param {string} url
 *      url to api call for delete
 *
 * @param {string} reference
 *      duplicate of components ref
 *
 * @param {object} selected
 *      @var {bool} all
 *
 *      @var {array} data
 *
 *      @var {number} entryCount
 *
 *      @var {array} tags
 *
 * @param {function} onToggleForm
 *
 * @param {function} onRefreshList
 *
 */
export class DeleteListItem extends React.Component {
  state = {
    active: false,
    step: 0,
    error: "",
    loading: false,
  };

  show = (show) => {
    this.setState({ active: show, error: "" });
  };

  onCancel = (index) => {
    this.props.onToggleForm(this.props.reference, false);
    this.setState({ step: 0, error: "" });
  };

  onDelete = (index) => {
    const numEntries = this.props.selected.all
      ? this.props.selected.entryCount - this.props.selected.data.length
      : this.props.selected.data.length;
    if (numEntries == 0) {
      this.setState({ error: "Please select at least 1 entry to be deleted" });
      return 0;
    }
    this.setState({ step: 1, error: "" });
  };

  onConfirm = (index) => {
    this.setState({ loading: true });
    u.post({
      url: this.props.url,
      data: {
        selected: this.props.selected,
      },
      success: (callback) => {
        this.props.onRefreshList();
        this.props.onToggleForm(this.props.reference, false);
        this.setState({ step: 0, loading: false });
      },
      error: (error, status) => {
        this.setState({ error: error, loading: false });
      },
    });
  };

  render = () => {
    const entries = this.props.selected.all
      ? this.props.selected.entryCount - this.props.selected.data.length
      : this.props.selected.data.length;

    return (
      <Segment
        ref="segment"
        active={this.state.active}
        align="center"
        scrollAnchor={this.props.scrollAnchor}
      >
        <div className="list-delete">
          <div className="segment-title">Delete Entries</div>
          <div className="segment-error">{this.state.error}</div>
          <Step active={this.state.step == 0}>
            <div className="segment-content">
              <h3>
                Please select the entries you wish to delete from the list below
              </h3>
              <div className="segment-selected">
                {entries} {entries == 1 ? "entry" : "entries"} selected
              </div>
              <div className="segment-buttons">
                <span className="mobile-only">
                  <Button type="medium" onClick={this.onCancel}>
                    Cancel
                  </Button>
                </span>
                <Button type="medium" onClick={this.onDelete}>
                  Delete
                </Button>
              </div>
            </div>
          </Step>

          <Step active={this.state.step == 1}>
            <div className="segment-content">
              <LoadGraphic
                active={this.state.loading}
                text="Deleting entries..."
              />
              <h3>
                Confirm that you wish to permanently delete {entries}{" "}
                {entries == 1 ? "entry" : "entries"}?
              </h3>
              <div className="segment-note">
                <i>
                  Note: This action is irreversible. Please double check before
                  deleting any entries
                </i>
              </div>
              <div className="segment-buttons">
                <Button type="medium" onClick={this.onCancel}>
                  Cancel
                </Button>
                <Button type="medium" onClick={this.onConfirm}>
                  Confirm
                </Button>
              </div>
            </div>
          </Step>
        </div>
      </Segment>
    );
  };
}
DeleteListItem.propTypes = {
  url: PropTypes.string.isRequired,
  reference: PropTypes.string.isRequired,
  selected: PropTypes.object.isRequired,
  onToggleForm: PropTypes.func.isRequired,
  onRefreshList: PropTypes.func.isRequired,
  scrollAnchor: PropTypes.string,
};

/**
 * Creates a page menu item for deleting list entries.
 *
 * @param {string} url
 *      url to api call for delete
 *
 * @param {string} reference
 *      duplicate of components ref
 *
 * @param {object} selected
 *      @var {bool} all
 *
 *      @var {array} data
 *
 *      @var {number} entryCount
 *
 *      @var {array} tags
 *
 * @param {function} onToggleForm
 *
 * @param {function} onRefreshList
 *
 */
export class GenerateSpreadsheet extends React.Component {
  state = {
    active: false,
    step: 0,
    error: "",
    estimation: 0,
    options: this.props.options,
    filename: "",
    filestamp: "",
    loading: false,
  };

  static getDerivedStateFromProps = (props, state) => {
    if (state.options != props.options) {
      state.options = props.options;
    }
    return state;
  };

  show = (show) => {
    this.setState({ active: show, error: "", step: 0 });
  };

  onResize = () => {};

  onCancel = (index) => {
    this.props.onToggleForm(this.props.reference, false);
    this.setState({ step: 0, error: "" });
  };

  onChangeOption = (value, label, index) => {
    let options = this.state.options;
    if (value == true && options[index].exclusive) {
      for (var i = 0; i < options.length; i++) {
        if (i != index && options[i].exclusive == options[index].exclusive) {
          options[i].selected = false;
        }
      }
    }
    options[index].selected = value;
    this.setState({ options: options });
  };

  onConfirm = (index) => {
    const numEntries = this.props.selected.all
      ? this.props.selected.entryCount - this.props.selected.data.length
      : this.props.selected.data.length;
    if (numEntries == 0) {
      this.setState({ error: "Please select at least 1 entry to continue" });
      return 0;
    }

    this.setState({ error: "", loading: true });
    u.post({
      url: this.props.url,
      data: {
        selected: this.props.selected,
        fields: this.state.options,
      },
      success: (callback) => {
        if (callback.ready) {
          u.download(
            callback.filename,
            callback.filestamp,
            () => {
              this.setState({
                step: 1,
                filestamp: callback.filestamp,
                filename: callback.filename,
                loading: false,
              });
            },
            (error, status) => {
              this.setState({ error: error, loading: false });
            },
            { serverURL: callback.serverURL }
          );
        } else {
          this.setState({
            step: 2,
            estimation: callback.estimation,
            loading: false,
          });
        }
      },
      error: (error, status) => {
        this.setState({ error: error });
      },
    });
  };

  onDownload = (index) => {
    u.download(
      this.state.filename,
      this.state.filestamp,
      (callback) => {},
      (error, status) => {
        this.setState({ error: error });
      }
    );
  };

  render = () => {
    const entries = this.props.selected.all
      ? this.props.selected.entryCount - this.props.selected.data.length
      : this.props.selected.data.length;
    const divMap =
      this.state.options.length == 0
        ? []
        : u.mapArrayToDivisions(this.state.options.length);
    return (
      <Segment
        ref="segment"
        active={this.state.active}
        align="center"
        scrollAnchor={this.props.scrollAnchor}
      >
        <div className="list-spreadsheet">
          <div className="segment-title">Convert to Spreadsheet</div>
          <div className="segment-error">{this.state.error}</div>
          <Step active={this.state.step == 0}>
            <div className="segment-content">
              <LoadGraphic
                active={this.state.loading}
                text="Preparing spreadsheet..."
              />
              <h3>
                Please select the entries you wish to download from the list
                below
              </h3>
              <div className="segment-selected">
                {entries} {entries == 1 ? "entry" : "entries"} selected
              </div>
              <div className="spreadsheet-options">
                <div>Please select the fields you want to include:</div>
                {divMap.map((range, index) => {
                  return (
                    <div key={index} className="spreadsheet-option-division">
                      {this.state.options.map((option, oIndex) => {
                        return oIndex >= range.start && oIndex < range.end ? (
                          <div className="spreadsheet-option" key={oIndex}>
                            <div className="spreadsheet-option-input">
                              <Checkbox
                                label="spreadsheet-option"
                                index={oIndex + ""}
                                values={{
                                  value: option.selected,
                                  enabled: option.enabled,
                                }}
                                onChange={this.onChangeOption}
                              />
                            </div>
                            <div className="spreadsheet-option-label">
                              {option.label}
                            </div>
                          </div>
                        ) : (
                          ""
                        );
                      })}
                    </div>
                  );
                })}
              </div>
              <div className="segment-buttons">
                <span className="mobile-only">
                  <Button type="medium" onClick={this.onCancel}>
                    Cancel
                  </Button>
                </span>
                <Button type="medium" onClick={this.onConfirm}>
                  Download
                </Button>
              </div>
            </div>
          </Step>

          <Step active={this.state.step == 1}>
            <div className="segment-content">
              <h3>Spreadsheet successfully generated</h3>
              <div className="segment-note">
                <i>
                  Note: If your download does not automatically start, please
                  click{" "}
                  <span className="segment-link" onClick={this.onDownload}>
                    here
                  </span>
                </i>
              </div>
              <div className="segment-buttons">
                <Button type="medium" onClick={this.onCancel}>
                  Close
                </Button>
              </div>
            </div>
          </Step>

          <Step active={this.state.step == 2}>
            <div className="segment-content">
              <h3>
                This spreadsheet will take{" "}
                <span className="segment-bad-emphasis">
                  ~{this.state.estimation} minutes
                </span>{" "}
                to generate
              </h3>
              <i>
                Note: A download link will be sent to your email upon completion
              </i>
              <div className="segment-buttons">
                <Button type="medium" onClick={this.onCancel}>
                  Close
                </Button>
              </div>
            </div>
          </Step>
        </div>
      </Segment>
    );
  };
}
GenerateSpreadsheet.propTypes = {
  url: PropTypes.string.isRequired,
  reference: PropTypes.string.isRequired,
  selected: PropTypes.object.isRequired,
  onToggleForm: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  scrollAnchor: PropTypes.string,
};

/**
 * Creates a themed add to list component that has a dropzone attached to it
 *
 * @param {string} label
 *      component name, required to uniquely identify this component on page
 *
 * @param {string} index
 *      component identifier, required to uniquely identify this component on page when labels are overlapping
 *
 * @param {string} url
 *      api request url to add new entries
 *
 * @param {number} addType
 *      1 - No Bulk Add
 *      2 - No Single Add
 *      3 - Both Add Available
 *
 * @param {string} reference
 *      duplicate of ref in parent component
 *
 * @param {function} onToggleForm
 *
 * @param {function} onRefreshList
 *
 * @param {function} onComplete
 *
 * @param {string} bulkURL
 *      api request url to add bulk entries
 *
 * @param {array} filetypes
 *      filetypes available for dropzone
 *
 * @function onValidated(data = object)
 *      Sends create to respective api call
 *
 * @function onCancel
 *      resets the component and hides it
 *
 * For easy instantiated -- use the following object
 *
 *  addData = {
 *      label:"add-entry",
 *      index:"0",
 *      addType:3,
 *      url:"",
 *      bulkURL:"",
 *      reference:"add",
 *      filetypes:Spreadsheet
 *  }
 */
export class AddListItem extends React.Component {
  state = {
    active: false,
    step: this.props.addType == 2 ? 1 : 0,
    entryCount: 0,
    loading: false,
    loadText: "",
  };

  show = (show) => {
    this.setState({ active: show, error: "" });
  };

  onBack = (step) => {
    let nextStep = this.state.step;

    if (step) {
      nextStep = step;
    } else if (nextStep - 1 >= 0) {
      nextStep--;
    }
    this.setState({ step: nextStep, error: "" });
  };

  onBulkUpload = () => {
    this.setState({ step: 1, error: "" });
  };

  onCancel = () => {
    if (this.state.step == 2) {
      if (this.props.addType == 2) {
        this.setState({ step: 1 });
      } else {
        this.setState({ step: 0 });
      }
    }
    this.setState({ error: "" });
    this.props.onToggleForm(this.props.reference, false);
  };

  onValidated = (data) => {
    this.setState({ loading: true });
    u.post({
      url: this.props.url,
      data: {
        fields: data,
      },
      success: (countOrID) => {
        this.setState({
          entryCount: countOrID,
          step: 2,
          loading: false,
          type: "id",
        });
        this.props.onRefreshList();
        if (this.props.onComplete) {
          this.props.onComplete(countOrID);
        }
      },
      error: (error, status) => {
        this.setState({ error: error, loading: false });
      },
    });
  };

  onFileAdded = (filename) => {
    this.setState({
      loading: true,
      userfilename: filename,
      loadText: "Uploading " + filename + "...",
      error: "",
    });
  };

  onFileUploaded = (filename) => {
    if (this.props.onUploadBypass) {
      this.setState({ step: 0, loading: false, error: "" });
      this.props.onUploadBypass(filename, this.state.userfilename);
    } else {
      this.setState({ loadText: "Attempting to create entries..." });
      u.post({
        url: this.props.bulkURL,
        data: {
          additional: this.props.additional,
          filename: filename,
        },
        success: (numEntries) => {
          this.setState({
            loading: false,
            step: 2,
            entryCount: numEntries,
            type: "count",
          });
          this.props.onRefreshList();
        },
        error: (error, status) => {
          this.setState({ loading: false, error: error });
        },
      });
    }
  };

  onFileError = (error) => {
    this.setState({ loading: false, error: error });
  };

  onFileProgress = (progress) => {
    this.setState({ loadText: this.state.loadText + progress + "%" });
  };

  render = () => {
    return (
      <Segment
        active={this.state.active}
        align="center"
        style={this.props.inList ? { border: "None" } : {}}
        scrollAnchor={this.props.scrollAnchor}
      >
        <div className="list-add">
          {this.props.inList ? (
            ""
          ) : (
            <div className="segment-title">
              {this.props.title ? this.props.title : "Add Entry"}
            </div>
          )}
          <div className="segment-error">{this.state.error}</div>

          <Step active={this.state.step == 0}>
            <div>
              <div className="segment-content">
                <LoadGraphic
                  active={this.state.loading}
                  text="Creating new entries.."
                />
                {this.props.children}
              </div>
              {this.props.addType > 1 ? (
                <div className="segment-add-buttons">
                  <TextButton onClick={this.onBulkUpload}>
                    Upload File
                  </TextButton>
                </div>
              ) : (
                ""
              )}
            </div>
          </Step>

          <Step active={this.state.step == 1}>
            <div>
              <div className="segment-content">
                <LoadGraphic
                  active={this.state.loading}
                  text={this.state.loadText}
                />
                <Dropzone
                  accessibleForPreview={false}
                  onAdded={this.onFileAdded}
                  onUploaded={this.onFileUploaded}
                  onError={this.onFileError}
                  onProgress={this.onFileProgress}
                  filetypes={this.props.filetypes}
                  label={this.props.label + "-dropzone-" + this.props.index}
                />
              </div>
              {this.props.addType != 2 ? (
                <div className="segment-buttons">
                  <Button type="medium" onClick={this.onBack}>
                    Back
                  </Button>
                </div>
              ) : (
                <div className="segment-buttons">
                  <span className="mobile-only">
                    <Button type="medium" onClick={this.onCancel}>
                      Cancel
                    </Button>
                  </span>
                </div>
              )}
            </div>
          </Step>

          <Step active={this.state.step == 2}>
            <SegmentComplete onComplete={this.onCancel}>
              {this.props.success ? (
                this.props.success
              ) : (
                <span>
                  Successfully created{" "}
                  <span className="segment-emphasis">
                    {this.state.entryCount}
                  </span>
                  {this.state.type == "count"
                    ? " new " +
                      (this.state.entryCount == 1 ? "entry" : "entries")
                    : ""}
                </span>
              )}
            </SegmentComplete>
          </Step>
        </div>
      </Segment>
    );
  };
}
AddListItem.propTypes = {
  onToggleForm: PropTypes.func.isRequired,
  onRefreshList: PropTypes.func.isRequired,
  onUploadBypass: PropTypes.func,
  /************ Optionals *************/
  onComplete: PropTypes.func,
  label: PropTypes.string,
  index: PropTypes.string,
  reference: PropTypes.string,
  addType: PropTypes.number.isRequired,
  url: PropTypes.string,
  bulkURL: PropTypes.string,
  filetypes: PropTypes.array,
  inList: PropTypes.bool,
  success: PropTypes.string,
  title: PropTypes.string,
  scrollAnchor: PropTypes.string,
};
AddListItem.defaultProps = {
  label: "add-entry",
  index: "0",
  filetypes: Spreadsheet,
  inList: false,
};

/**
 *  Creates a general menu item that allows for photo upload
 *
 *  @param {string} reference
 *      ref defined in parent object
 *
 *  @param {string} fieldname
 *      returned when the file has been uploaded
 *
 *  @param {func} onToggleForm
 *      control menu button
 *
 *  @param {func} onUpload(filename,fieldname)
 *      called once file has been successfully uploaded
 *
 *  @function show(bool)
 *      show or hide this component
 */
export class UploadPhoto extends React.Component {
  state = {
    active: false,
    step: 0,
    isMobile: false,
    currentVideoSource: "",
    cameraVideoSourceOptions: [],
  };

  webcam = null;

  dropzone = null;

  componentDidMount = () => {
    this.setState({ isMobile: u.isMobileDevice() });
  };

  show = (show) => {
    this.setState({ active: show });
  };

  onGoToWebcam = () => {
    this.setState({ error: "", step: 1 });
  };

  onChangeCamera = (value, label, index, option) => {
    this.setState({ currentVideoSource: value });
  };

  onPromptSources = (sources, selectedSource) => {
    if (this.state.cameraVideoSourceOptions.length == 0 && sources.length > 0) {
      let s = [];
      for (var i = 0; i < sources.length; i++) {
        s.push({ value: sources[i].deviceId, label: sources[i].label });
      }

      this.setState({
        cameraVideoSourceOptions: s,
        currentVideoSource: selectedSource,
      });
    }
  };

  checkPhoto = (ref) => {
    if (this.state.photoAdded) {
      ref.onFileAdded(null, this.state.photoAdded);
      this.setState({ photoAdded: undefined });
    }
  };

  onCameraCapture = () => {
    const image = this.webcam.getScreenshot();
    this.setState({ step: 0, error: "", photoAdded: image });
  };

  onBack = () => {
    this.setState({ error: "", step: 0 });
  };

  onCancel = () => {
    this.props.onToggleForm(this.props.reference, false);
  };

  onPhotoUploaded = (filename, callback) => {
    u.post({
      url: this.props.url,
      data: {
        id: this.props.id,
        filename: filename,
      },
      success: (url) => {
        callback();
        this.props.onUpload(url, this.props.fieldname);
        this.props.onToggleForm(this.props.reference, false);
      },
      error: (error, status) => {
        this.setState({ error: error });
      },
    });
  };

  render = () => {
    return (
      <Segment
        active={this.state.active}
        align="center"
        scrollAnchor={this.props.scrollAnchor}
      >
        <div>
          {this.props.noTitle ? (
            ""
          ) : (
            <div className="segment-title">Upload Photo</div>
          )}
          <div className="segment-error">{this.state.error}</div>
          <Step active={this.state.step == 0}>
            <ImageDropzone
              ref={(c) => {
                this.checkPhoto(c);
              }}
              onUpload={this.onPhotoUploaded}
            />
            {!this.state.isMobile ? (
              <div
                className="upload-webcam"
                style={{ padding: "20px", fontSize: "0.85em" }}
              >
                <TextButton onClick={this.onGoToWebcam}>Use Webcam</TextButton>
              </div>
            ) : (
              ""
            )}
            <div className="segment-buttons">
              <span className="mobile-only">
                <Button type="medium" onClick={this.onCancel}>
                  Cancel
                </Button>
              </span>
            </div>
          </Step>
          <Step active={this.state.step == 1}>
            <h3>
              If video is not streaming below, please make sure you have a
              webcam attached and refresh the page.
            </h3>
            <div
              style={{
                width: "500px",
                maxWidth: "90%",
                margin: "20px auto",
                fontSize: "0.85em",
              }}
            >
              <Select
                label="Camera Source"
                values={{
                  options: this.state.cameraVideoSourceOptions,
                  value: this.state.currentVideoSource,
                }}
                onChange={this.onChangeCamera}
              />
            </div>
            <Webcam
              ref={(c) => (this.webcam = c)}
              audio={false}
              width={640}
              screenshotFormat="image/png"
              onPromptSources={this.onPromptSources}
              videoSource={this.state.currentVideoSource}
              videoOnly="true"
            />
            <div style={{ margin: "20px auto" }}>
              <Button type="medium" onClick={this.onBack}>
                Back
              </Button>
              <Button type="medium" onClick={this.onCameraCapture}>
                Capture
              </Button>
            </div>
          </Step>
        </div>
      </Segment>
    );
  };
}
UploadPhoto.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  url: PropTypes.string.isRequired,
  reference: PropTypes.string.isRequired,
  fieldname: PropTypes.string.isRequired,
  onUpload: PropTypes.func.isRequired,
  onToggleForm: PropTypes.func.isRequired,
  noTitle: PropTypes.bool,
  scrollAnchor: PropTypes.string,
};

/**
 * Creates a remark object to be placed in a databox
 *
 * @param {array} content
 *
 * @param {func} onChange
 *
 * @param {string} id
 *      entity id
 *
 * @param {string} addURL
 *      url to push new remarks
 *
 * @param {string} deleteURL
 *      url to push delete remarks
 *
 * @param {number} permissions
 *      numbered permission value
 */
export class Remarks extends React.Component {
  state = {
    error: "",
    step: 0,
    loading: false,
  };

  onRemove = (e) => {
    if (this.props.permissions != 2) {
      this.setState({
        error: "Error! You do not have permissions to delete remarks",
      });
      return 0;
    }

    const removeID = parseInt(e.currentTarget.id);
    this.setState({ error: "" });
    const remark = this.props.content[removeID];

    u.post({
      url: this.props.deleteURL,
      data: {
        id: this.props.id,
        remark: remark,
      },
      success: (callback) => {
        let remarks = this.props.content;
        remarks.splice(removeID, 1);
        this.setState({ loading: false, step: 0 });
        this.props.onChange(remarks, "remarks", this.props.index, (error) => {
          this.setState({ error: error, loading: false });
        });
      },
      error: (error, status) => {
        this.setState({ error: error, loading: false });
      },
    });
  };

  onAddRequest = () => {
    this.setState({ step: 1, error: "" });
  };

  onAdded = () => {
    this.setState({ error: "" });
    const remark = this.refs["newRemark"].validate();
    if (!remark.valid) {
      this.setState({ error: remark.value });
      return 0;
    }

    let data = {
      dateTime: moment().format("YYYY-MM-DDTHH:mm:ss"),
      staffID: u.getUserID(),
      staffName: u.getFullName(),
      data: remark.value,
    };

    u.post({
      url: this.props.addURL,
      data: {
        id: this.props.id,
        remark: data,
      },
      success: (callback) => {
        data.id = callback;
        let remarks = this.props.content;
        remarks.unshift(data);
        this.setState({ loading: false, step: 0 });
        this.props.onChange(remarks, "remarks", this.props.index, (error) => {
          this.setState({ error: error, loading: false });
        });
      },
      error: (error, status) => {
        this.setState({ error: error, loading: false });
      },
    });
  };

  onCancel = () => {
    this.setState({ step: 0, error: "" });
  };

  render = () => {
    return (
      <div className="remark-container">
        <LoadGraphic active={this.state.loading} text="Updating database..." />
        <Step active={this.state.step == 0}>
          <div>
            {this.props.content.length > 0 ? (
              this.props.content.map((remark, index) => {
                return (
                  <div className="remark" key={index}>
                    <div className="remark-remove">
                      <div
                        className="remark-remove-image"
                        id={"" + index}
                        onClick={this.onRemove}
                        title="Delete Remark"
                      >
                        <div className="remark-remove-line"></div>
                      </div>
                    </div>
                    <div className="remark-date">
                      {moment(remark.dateTime).format("DD MMM YY - HH:mma")}
                    </div>
                    <div className="remark-staff">
                      <Link
                        to="/staff"
                        id={"staffID-" + remark.staffID}
                        onClick={u.setLinkContext}
                        onContextMenu={u.setLinkContext}
                      >
                        {remark.staffName}
                      </Link>
                    </div>
                    <div className="remark-data">{remark.data}</div>
                  </div>
                );
              })
            ) : (
              <div className="remarks-placeholder">No Remarks</div>
            )}
          </div>
        </Step>
        <Step active={this.state.step == 1}>
          <div style={{ textAlign: "center" }}>
            <div style={{ width: "700px", maxWidth: "100%", margin: "auto" }}>
              <h2>Add Remark</h2>
              <div className="overview-list-error">{this.state.error}</div>
              <div>
                <Area
                  ref="newRemark"
                  label="Remarks"
                  rules={{ required: true, rows: 5 }}
                />
              </div>
              <div className="segment-note">
                Note. System will only store up to 10 remarks at a time
              </div>
              <div className="segment-buttons">
                <Button type="medium" onClick={this.onCancel}>
                  Cancel
                </Button>
                <Button type="medium" onClick={this.onAdded}>
                  Add
                </Button>
              </div>
            </div>
          </div>
        </Step>
      </div>
    );
  };
}
Remarks.propTypes = {
  content: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  addURL: PropTypes.string.isRequired,
  deleteURL: PropTypes.string.isRequired,
  permissions: PropTypes.number.isRequired,
  id: PropTypes.string.isRequired,
};

/**
 * Creates a themed acknowledgement to display at the end of segments
 *
 * @param {html} children
 *
 * @param {function} onComplete
 *
 */
export class SegmentComplete extends React.Component {
  render = () => {
    return (
      <div className="segment-content">
        <div>
          <img
            src="/images/input/confirm.png"
            style={{
              display: "inline-block",
              verticalAlign: "middle",
              width: "50px",
              margin: "25px",
            }}
          />
          <h3
            style={{
              display: "inline-block",
              verticalAlign: "middle",
              margin: "0",
            }}
          >
            {this.props.children}
          </h3>
        </div>
        {this.props.onComplete ? (
          <div className="segment-buttons">
            <Button type="medium" onClick={this.props.onComplete}>
              Close
            </Button>
          </div>
        ) : (
          ""
        )}
      </div>
    );
  };
}
SegmentComplete.propTypes = {
  onComplete: PropTypes.func,
};

/**
 * Creates a themed resolution stack area
 */
export class ResolutionStack extends React.Component {
  state = { active: false };

  toggleActive = () => {
    this.setState({ active: !this.state.active });
  };

  render = () => {
    return (
      <div>
        <Step active={!this.state.active}>
          <TextButton onClick={this.toggleActive}>
            See Resolution Stack
          </TextButton>
        </Step>
        <Step active={this.state.active}>
          <div className="resolution-stack">
            <div style={{ margin: "10px 0" }}>
              <TextButton onClick={this.toggleActive}>
                Close Resolution Stack
              </TextButton>
            </div>
            {this.props.children}
          </div>
        </Step>
      </div>
    );
  };
}

/**
 * Creates a themed resolution item to place resolving staff into
 *
 * @param {object} content
 *      @var {string} label
 *           "Completed" on
 *
 *      @var {string} date
 *
 *      @var {string} staffID
 *
 *      @var {string} avatarLink
 *
 *      @var {string} name
 */
export class ResolutionItem extends React.Component {
  render = () => {
    const content = this.props.content;
    return (
      <div className="resolution-item">
        <div className="resolution-title">{content.label}</div>
        <div>{moment(content.date).format("DD MMM YY")}</div>
        {content.staffID && content.staffID != "-1" ? (
          <Link to={"/staff/" + content.staffID}>
            <img
              src={u.getAvatarLink(content.avatarLink)}
              className="resolution-image"
            />
            <div className="resolution-name">{content.name}</div>
          </Link>
        ) : (
          <div>
            <img src={"/images/app/system.png"} className="resolution-image" />
            <div className="resolution-name">System</div>
          </div>
        )}
      </div>
    );
  };
}
ResolutionItem.propTypes = {
  content: PropTypes.shape({
    label: PropTypes.string,
    date: PropTypes.string,
    staffID: PropTypes.string,
    avatarLink: PropTypes.string,
    name: PropTypes.string,
  }),
};

/**
 * Themed photo upload dropzone, different from image dropzone which allows users to edit image
 * Photo dropzone allows users to append comments and stores photo without editting aspect ratio
 *
 * @param {function} onCancel
 *
 * @param {function} onAdded
 */
export class PhotoDropzone extends React.Component {
  state = {
    error: "",
    step: 0,
    scale: 120,
    rotate: 0,
    loading: false,
    loadText: "Uploading...",
    dimensions: { width: 512, height: 512 },
    ratingOptions: [],
  };

  onDropzoneError = (error) => {
    this.setState({ error: error, loading: false });
  };

  onAdded = (filename, file) => {
    this.setState({ step: 1, error: "", src: file, userFilename: filename });
  };

  onBack = (step) => {
    this.setState({ step: parseInt(step), error: "" });
  };

  onAddPhoto = () => {
    this.setState({ error: "" });

    let comments = this.refs["photoComments"].validate();
    if (!comments.valid) {
      this.setState({ error: comments.value });
      return 0;
    }

    const others = this.refs["photoCommentsOthers"]
      ? this.refs["photoCommentsOthers"].validate()
      : { valid: true };
    if (!others.valid) {
      this.setState({ error: others.value });
      return 0;
    }

    const category = this.refs["photoCategory"]
      ? this.refs["photoCategory"].validate()
      : {};

    this.setState({ loading: true });

    this.refs["editor"].getImageScaledToCanvas().toBlob(
      (blob) => {
        let f = new File([blob], "editted-image.jpg", {
          type: "image/jpeg",
          lastModifier: moment().format("YYYY-MM-DD"),
        });
        this.uploadEvent = u.upload({
          file: f,
          data: {
            accessibleForPreview: true,
          },
          uploadProgress: (e) => {
            if (e.lengthComputable) {
              let loaded = Math.round((e.loaded / e.total) * 100);
              this.setState({ loadText: "Uploading " + loaded + "%..." });
            }
          },
          success: (filename) => {
            let photo = {
              filename: filename,
              comments: others.value ? others.value : comments.value,
              severity: comments.option ? comments.option.severity : undefined,
              chargeType: comments.option
                ? comments.option.chargeType
                : undefined,
              category: category.value,
              src: this.state.src,
              userFilename: this.state.userFilename,
            };
            this.props.onAdded(photo);
            this.setState({ loading: false, step: 0 });
          },
          error: (error, status) => {
            this.setState({ error: error, loading: false });
          },
        });
      },
      "image/jpeg",
      1
    );
  };

  onCancel = () => {
    this.props.onCancel(this.props.index);
  };

  onChangeCategory = (value, label, index, option) => {
    this.setState({
      photoCategory: value,
      ratingOptions: option.ratingOptions,
    });
  };

  onChangePhotoComments = (value) => {
    this.setState({ photoComments: value });
  };

  render = () => {
    let { width, height } = this.state.dimensions;
    if (width > 512) {
      width = 512;
    }
    return (
      <div style={{ position: "relative" }}>
        <LoadGraphic active={this.state.loading} text={this.state.loadText} />
        <div className="page-error">{this.state.error}</div>
        <Step active={this.state.step == 0}>
          <div style={{ position: "relative", textAlign: "center" }}>
            <h3>Add Photo</h3>
            <Dropzone
              onError={this.onDropzoneError}
              accessibleForPreview={false}
              filetypes={Photo}
              label="Photo Upload"
              onAdded={this.onAdded}
              noUpload={true}
            />
            {this.props.onCancel ? (
              <div style={{ margin: "20px auto" }}>
                <Button type="medium" onClick={this.onCancel}>
                  Back
                </Button>
              </div>
            ) : (
              ""
            )}
          </div>
        </Step>
        <Step active={this.state.step == 1}>
          <div style={{ textAlign: "center" }}>
            <h3>Add Photo</h3>

            <Editor
              ref="editor"
              image={this.state.src}
              width={width - 20}
              height={width - 20}
              border={10}
              color={[255, 0, 0, 0.5]} // RGBA
              scale={this.state.scale / 100}
              rotate={this.state.rotate}
            />

            <Measure
              bounds
              onResize={(contentRect) => {
                this.setState({ dimensions: contentRect.bounds });
              }}
            >
              {({ measureRef }) => (
                <div
                  ref={measureRef}
                  style={{ width: "532px", maxWidth: "100%", margin: "auto" }}
                />
              )}
            </Measure>

            <div style={{ width: "532px", maxWidth: "100%", margin: "auto" }}>
              <div
                className="image-dropzone-slider"
                style={{ margin: "10px auto" }}
              >
                <div className="image-dropzone-slider-title">Zoom</div>
                <ProgressSlider
                  min={50}
                  max={300}
                  value={this.state.scale}
                  onChange={(value) => {
                    this.setState({ scale: value });
                  }}
                />
              </div>
              <div
                className="image-dropzone-slider"
                style={{ margin: "10px auto" }}
              >
                <div className="image-dropzone-slider-title">Rotate</div>
                <ProgressSlider
                  min={-180}
                  max={180}
                  value={this.state.rotate}
                  onChange={(value) => {
                    this.setState({ rotate: value });
                  }}
                />
              </div>
              {this.props.rating ? (
                <div>
                  <div>
                    <Select
                      ref="photoCategory"
                      label="Category"
                      values={{ options: CenturionInspectionOptions }}
                      onChange={this.onChangeCategory}
                    />
                  </div>

                  <div style={{ margin: "10px auto 0 auto" }}>
                    <Select
                      ref="photoComments"
                      label="Photo Comments"
                      field="photoComments"
                      values={{ options: this.state.ratingOptions }}
                      rules={{ defaultValue: "Passed inspection standard" }}
                      onChange={this.onChangePhotoComments}
                    />
                  </div>

                  {this.state.photoComments == "Others" ? (
                    <div style={{ margin: "5px auto 0 auto" }}>
                      <Box
                        ref="photoCommentsOthers"
                        label="Photo Comment Others"
                        rules={{ required: true }}
                      />
                    </div>
                  ) : (
                    ""
                  )}
                </div>
              ) : (
                <div>
                  <Area
                    ref="photoComments"
                    label="Photo Comments"
                    rules={{
                      rows: 5,
                      placeholder:
                        "Please add some comments to describe this photo...",
                    }}
                  />
                </div>
              )}
            </div>

            <div style={{ margin: "20px auto" }}>
              <Button type="medium" index="0" onClick={this.onBack}>
                Back
              </Button>
              <Button type="medium" onClick={this.onAddPhoto}>
                Add
              </Button>
            </div>
          </div>
        </Step>
      </div>
    );
  };
}
PhotoDropzone.propTypes = {
  onCancel: PropTypes.func,
  index: PropTypes.string,
  onAdded: PropTypes.func.isRequired,
  rating: PropTypes.bool,
};

export class EmptyAvatar extends React.Component {
  state = {
    value: "",
    color: "",
    borderColor: {},
  };

  componentDidMount = () => {
    //Select Random Color
    const value = this.props.value.substr(0, 1).toUpperCase();
    const colors = [
      "#3DB34C",
      "#B32B4F",
      "#4C31B8",
      "#FB8AFF",
      "#3387B8",
      "#FF7096",
      "#45ABB8",
    ];
    const colorIndex = parseInt(value.charCodeAt(0)) % colors.length;
    const color = colors[colorIndex];
    const borderColor = u.hexToRgb(color);
    this.setState({ value: value, color: color, borderColor: borderColor });
  };

  render = () => {
    return (
      <div
        style={{
          position: "relative",
          width: "30px",
          height: "30px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: this.state.color,
          borderRadius: "50%",
        }}
      >
        <div
          style={{
            color: "white",
            display: "inline-block",
            fontSize: "1em",
            margin: "auto",
          }}
        >
          {this.state.value}
        </div>
      </div>
    );
  };
}
EmptyAvatar.propTypes = {
  value: PropTypes.string,
};
